import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import ShowSelectedImage from "../ShowSelectedImage";
import ApiLinks from "../../api/apis";
import { useTranslation } from "react-i18next";
import useCoverLogoStore from "../../store/useCoverLogoStore";
import toast from 'react-hot-toast';
import axios from "axios";
import Loader from "../../assets/Loading";
import useLocaleStore from "../../store/useLocaleStore";

const hostUrl = ApiLinks.ActiveLink();
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

const CoverLogoUpload = () => {
    const { t } = useTranslation()
    const { locale } = useLocaleStore();
    const [coverFiles, setCoverFiles] = useState([]);
    const [coverImage, setCoverImage] = useState("");
    const [coverImageFileActive, setCoverImageFileActive] = useState("");
    const [coverModalVisible, setCoverModalVisible] = useState(false);
    const [coverSpinner, setCoverSpinner] = useState(false);
    const [coverModalAdvance, setCoverModalAdvance] = useState(false);
    const [coverColors, setCoverColors] = useState();
    const [coverSelectedColors, setCoverSelectedColors] = useState([]);
    const { setLogo } = useCoverLogoStore()

    const onDropFunction = useCallback((acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            toast(t("Home.fileNotSupported"), {
                duration: 3000,
                icon: '⚠️',
            });
            return;
        }

        if (acceptedFiles.length > 0) {
            const selectedFile = acceptedFiles[0];
            // Explicitly reject .ps files
            if (selectedFile.name.toLowerCase().endsWith('.ps')) {
                toast(t("Home.fileNotSupported"), {
                    duration: 3000,
                    icon: '⚠️',
                });
                return;
            }

            if (
                selectedFile.type === "image/png" ||
                selectedFile.type === "image/svg+xml"
            ) {
                setCoverFiles(acceptedFiles);
                setCoverModalVisible(true);
                setCoverSpinner(true);

                var reader = new FileReader();
                reader.readAsDataURL(selectedFile);

                reader.onloadend = function () {
                    let base64data = reader.result;
                    setCoverImage(base64data);
                    setCoverSpinner(true);

                    // Process the cover logo (if needed)
                    fetch(`${hostUrl}images/process`, {
                        method: "POST",
                        headers: {
                            'Content-Type': "text/json"
                        },
                        body: JSON.stringify({
                            fileupload: base64data,
                        }),
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            setCoverImageFileActive(data.hasWhite ? data.upload : base64data);
                            setLogo(data.hasWhite ? data.upload : base64data)
                        })
                        .catch((err) => {
                            console.log("unable to process image " + err);
                            setCoverSpinner(false);
                            setCoverImage("");
                            toast.error("Error, please try a new logo.");
                        })
                        .finally(() => setCoverSpinner(false));
                };
            } else {
                toast.error("Invalid file type. Please upload a PNG or SVG image.");
            }
        }
    });

    const handleAdvanceImageProcessingSubmit = (event) => {
        event.preventDefault();
        setCoverModalAdvance(!coverModalAdvance);
        setCoverModalVisible(false);
        setCoverSpinner(true);

        const formData = new FormData();
        formData.append("fileupload", coverImage);

        axios.post(`${hostUrl}images/advprocess`, formData)
            .then(response => {
                setCoverColors(response.data.colors);
            })
            .catch(error => {
                console.error('Error:', error);
                toast.error(t("Home.errorInConversion"));
            })
            .finally(() => {
                setCoverSpinner(false);
            });
    };

    const handleCheckboxChange = (color) => {
        setCoverSelectedColors((prevSelectedColors) => {
            if (prevSelectedColors.includes(color)) {
                return prevSelectedColors.filter((selectedColor) => selectedColor !== color);
            } else {
                // Add color if not selected
                return [...prevSelectedColors, color];
            }
        });
    };

    const handleAdvanceSubmit = async (selectedColors) => {
        setCoverImageFileActive("");
        if (!selectedColors || selectedColors.length === 0) {
            setCoverImageFileActive(coverImage);
            setCoverModalAdvance(!coverModalAdvance);
            return;
        }
        try {
            setCoverModalAdvance(!coverModalAdvance);
            setCoverSpinner(true);

            const formData = new FormData();
            formData.append('fileupload', coverImage);
            formData.append("removecolors", JSON.stringify(selectedColors));

            const response = await axios.post(`${hostUrl}images/advresults`, formData);

            if (response.status === 200) {
                setCoverImageFileActive(response.data.proccessedImg);
                setLogo(response.data.proccessedImg); // Update the global logo state
            } else {
                console.error('API Error:', response.statusText);
            }
        } catch (error) {
            console.error('Axios Error:', error.message);
            toast.error(t("Home.errorInConversion"));
        } finally {
            setCoverSpinner(false);
            setCoverSelectedColors([]);
        }
    };

    const closeCoverManualModal = () => {
        if (coverSelectedColors.length < 1) {
            toast(t("Home.colorsToRemoved"), {
                duration: 3000,
                icon: '👏',
            });
        } else {
            setCoverModalAdvance(false);
        }
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: {
            'image/png': ['.svg', '.png']
        },
        maxFiles: 1,
        onDrop: onDropFunction,
        noClick: true,
        validator: (file) => {
            if (file.size > MAX_FILE_SIZE) {
                if (locale === "us" || locale === "en") {
                    toast("File is larger than 5MB")
                } else {
                    toast("Le fichier est plus grand que 5 Mo.")
                }
            }
            return null;
        }
    });

    return (
        <div>
            <p className="text-center fw-bold">{t("Home.coverLogoHeading")}</p>
            <div
                {...getRootProps({})}
                className="product-logo-box d-flex flex-column justify-content-center align-items-center p-2 border rounded"
                onClick={open}
            >
                <input {...getInputProps()} name="coverlogo" className="inputt" />
                <img
                    src="/images/image-plus.svg"
                    alt="uploaded-img"
                    className={`mt-auto add-image-icon ${coverFiles.length === 1 ? "hide" : ""}`}
                />
                <div className="d-flex justify-content-center selected-image flex-column justify-content-center align-items-center">
                    <div
                        className={`${coverFiles.length === 0 ? "hide" : "d-flex"
                            } align-items-center justify-content-between gap-5`}
                    >
                        <div className="w-50">
                            <span className="fw-bold">{t("Home.original")}</span>
                            <div className="margin-auto">
                                {coverSpinner ? (
                                    <Loader.BasicLoader class="mw-50" />
                                ) : (
                                    <ShowSelectedImage newimageone={coverImage} color={true} />
                                )}
                            </div>
                        </div>
                        <div className="w-50">
                            <span className="fw-bold">{t("Home.OneConversion")}</span>
                            <div className="margin-auto">
                                {coverSpinner ? (
                                    <Loader.BasicLoader class="mw-50" />
                                ) : (
                                    <ShowSelectedImage newimageone={coverImageFileActive} color={false} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={`mt-auto text-center text-uppercase ${coverFiles.length === 1 ? "hide" : ""
                        }`}
                >
                    <div className="btn bg-yellow">
                        {t("Home.coverLogo")}
                    </div>
                </div>
            </div>

            {/* Modal for Cover Logo */}
            {coverModalVisible && (
                <div id="coverLogoModal" className="modal open">
                    <div className="modal-content preview">
                        <span className="close" onClick={() => setCoverModalVisible(false)}>
                            &times;
                        </span>
                        <div className="scrollable d-flex align-items-start upload-modal-sm">
                            <div className="w-100">
                                <div className="d-flex justify-content-center align-items-center flex-column">
                                    <h2 className="text-center mb-2">{t("Home.coverLogoTitle")}</h2>
                                    <div className="d-flex align-items-center justify-content-between gap-5 flex-column flex-xl-row">
                                        <div className="w-50">
                                            <span className="fw-bold">{t("Home.original")}</span>
                                            {coverSpinner ? (
                                                <Loader.BasicLoader class="mw-50" />
                                            ) : (
                                                <ShowSelectedImage
                                                    newimageone={coverImage}
                                                    color={true}
                                                />
                                            )}
                                        </div>
                                        <div className="w-50">
                                            <span className="fw-bold">{t("Home.OneConversion")}</span>
                                            {coverSpinner ? (
                                                <Loader.BasicLoader class="mw-50" />
                                            ) : (
                                                <ShowSelectedImage
                                                    newimageone={coverImageFileActive}
                                                    color={false}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="w-full d-flex justify-content-around align-items-center gap-5 mt-4 flex-column flex-xl-row">
                                        <div className="upload-btn bg-gray" onClick={open}>
                                            {t("Home.reupload")}
                                        </div>
                                        <div
                                            className="upload-btn bg-yellow"
                                            onClick={handleAdvanceImageProcessingSubmit}
                                        >
                                            {t("Home.advanceImage")}
                                        </div>
                                        <div
                                            className="upload-btn bg-yellow"
                                            onClick={() => setCoverModalVisible((prev) => !prev)}
                                        >
                                            {t("Home.approve")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* <div className="line-container">
                                <div className="vl"></div>
                            </div> */}
                            {/* <div className="w-30 d-flex justify-content-center align-items-center">
                                <div className="info-desc mt-2">
                                    <h3 className="gray">
                                        <strong>For Best Results</strong>
                                    </h3>
                                    <p className="mt-3 mb-3">
                                        <strong className="gray semi-title">Background</strong> Keep
                                        your logo background transparent
                                    </p>
                                    <p className="mt-3 mb-3">
                                        <strong className="gray semi-title">Clarity</strong> Opt for
                                        a clear logo with high resolution
                                    </p>
                                    <p className="mt-3 mb-3">
                                        <strong className="gray semi-title">Simplicity</strong>{" "}
                                        Avoid complex logos with too many details
                                    </p>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Adjustment Modal for Cover Logo */}
            <div id="coverAdvanceImage" className={`modal ${coverModalAdvance ? "show" : ""}`}>
                <div className="modal-content preview advance-image">
                    <span className="close" onClick={closeCoverManualModal}>&times;</span>
                    <section>
                        <div className="scrollable d-flex justify-content-center">
                            <div className="w-70">
                                <div className="d-flex justify-content-evenly align-items-center flex-column gap-2">
                                    <h2 className="text-center mb-2">{t("Home.selectColors")}</h2>
                                    <div className="preview-image-background">
                                        <img className="manual-image-adjustment" src={coverImage} alt="uploaded cover" />
                                    </div>
                                    <div className="w-full d-flex justify-content-around align-items-center gap-5 mt-5 flex-wrap">
                                        {coverSpinner ? <p>{t("Home.analyzeColors")}</p> : (
                                            <>
                                                {coverColors?.map((color, index) => (
                                                    <div className="form-check" key={index + 1}>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`coverColorCheckbox${index}`}
                                                            checked={coverSelectedColors?.includes(color)}
                                                            onChange={() => handleCheckboxChange(color)}
                                                        />
                                                        <label htmlFor={`coverColorCheckbox${index}`} className="d-flex">
                                                            <div className="color-box" style={{ backgroundColor: `rgba(${color.join(', ')})` }}>
                                                                {/*add content inside the color-box if needed */}
                                                            </div>
                                                        </label>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                    <div className="w-100 d-flex justify-content-center align-items-center gap-2 mt-4">
                                        <div className={`upload-btn sm bg-gray`} onClick={closeCoverManualModal}>{t("Home.back")}</div>
                                        <div className={`upload-btn bg-yellow sm`} onClick={() => handleAdvanceSubmit(coverSelectedColors)}>{t("Home.removeColors")}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CoverLogoUpload;
